'use client';

import { useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  ConfirmationDialog,
  ListPageLayout,
  toasterApi,
} from '@terra/ui-design-system';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import {
  useAppTargetId,
  useTargetId,
  useTargetType,
} from '../../store/projectStore';
import {
  useFindingsCounts,
  useInvalidateFindings,
  useInvalidateFindingsCounts,
} from '../../hooks/api/useFindings';
import { useFindingsListPages } from '../../hooks/api/useFindingsListPages';
import { useInvalidateApplicationDomainTree } from '../../hooks/api/useApplicationDomainTree';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { CreateReportDialog } from '../dialogs/CreateReportDialog';
import { FindingDeleteConfirmationDialog } from './FindingDeleteConfirmationDialog';
import { FindingsContextNavigation } from './findings-header/FindingsContextNavigation';
import { FindingsFilterPanel } from './findings-header/FindingsFilterPanel';
import { FindingsViewOptionsMenu } from './findings-header/FindingsViewOptionsMenu';
import { FindingsBulkActionsBar } from './findings-header/FindingsBulkActionsBar';
import { resolveFindingsCreateChainAction } from './findings-header/resolve-findings-create-chain-action';
import { useFindingsCreateChainCopilot } from './findings-header/useFindingsCreateChainCopilot';
import { FindingsTable } from './findings-table/FindingsTable';
import {
  FindingsTableContextProvider,
  type FindingsTableContextValue,
} from './findings-table/FindingsTableContext';
import {
  resolveFindingsPublicationChip,
  toFindingsPublishedFilter,
  type FindingsColumnSort,
  type FindingsGroupSortOrder,
} from './findings-table/findings-list.model';
import { useFindingsListFilters } from './findings-table/useFindingsListFilters';
import { useFindingsSelection } from './findings-table/useFindingsSelection';
import { useFindingsTableModel } from './findings-table/useFindingsTableModel';
import { useFindingsTableNavigation } from './findings-table/useFindingsTableNavigation';
import { useFindingsRowStatusActions } from './findings-table/useFindingsRowStatusActions';
import { useFindingsDeleteAction } from './findings-table/useFindingsDeleteAction';
import { useFindingsBulkPublish } from './findings-table/useFindingsBulkPublish';
import { useFindingsReportDialog } from './findings-table/useFindingsReportDialog';

type FindingsContentProps = {
  endpointId?: string;
  endpointUrl?: string;
  lifecycleStateOverride?: LifecycleStateFilterDto;
  header?: ReactNode;
};

export function FindingsContent({
  endpointId,
  lifecycleStateOverride,
  header,
}: FindingsContentProps) {
  const tTable = useTranslations('proxy.findings.table');
  const tFindingsList = useTranslations('designSystem.findingsList');
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const tBulkConflict = useTranslations('findingsTab.bulkConflict');
  const tCreateReportDialog = useTranslations(
    'proxy.findings.createReportDialog',
  );
  const tCommon = useTranslations('common');
  const targetId = useTargetId();
  const targetType = useTargetType();
  const appTargetId = useAppTargetId();
  const invalidateFindings = useInvalidateFindings();
  const invalidateFindingsCounts = useInvalidateFindingsCounts();
  const invalidateApplicationDomainTree = useInvalidateApplicationDomainTree();
  const filters = useFindingsListFilters({
    endpointId,
    lifecycleStateOverride,
  });
  const list = useFindingsListPages({
    targetId,
    targetType,
    options: filters.listOptions,
  });
  const { matchingCount, totalCount } = useFindingsCounts({
    targetId,
    targetType,
    filter: filters.filter,
  });
  const selectionModel = useFindingsSelection({
    targetId,
    targetType,
    filter: filters.filter,
    findings: list.findings,
    persistedSelection: filters.selection,
    setPersistedSelection: filters.setSelection,
    resetKey: JSON.stringify([filters.filter, filters.columnSort]),
  });
  const tableModel = useFindingsTableModel({
    targetId,
    filter: filters.filter,
    findings: list.findings,
    groupByField: filters.groupByField,
    hasNextPage: list.hasNextPage,
  });
  const navigation = useFindingsTableNavigation({
    targetId,
    targetType,
    findings: list.findings,
    listOptions: filters.listOptions,
  });
  const refreshFindings = useCallback(async () => {
    await Promise.all([
      invalidateFindings(),
      invalidateFindingsCounts(),
      appTargetId
        ? invalidateApplicationDomainTree({ applicationId: appTargetId })
        : Promise.resolve(),
    ]);
  }, [
    appTargetId,
    invalidateApplicationDomainTree,
    invalidateFindings,
    invalidateFindingsCounts,
  ]);
  const statusActions = useFindingsRowStatusActions({
    onMutated: refreshFindings,
  });
  const deleteAction = useFindingsDeleteAction({
    selectionModel,
    onMutated: refreshFindings,
  });
  const bulkPublish = useFindingsBulkPublish({
    selectionModel,
    onMutated: refreshFindings,
  });
  const reportDialog = useFindingsReportDialog({
    selectionModel,
    loadedFindings: list.findings,
  });
  const { selection } = selectionModel;
  const sendCreateChainToCopilot = useFindingsCreateChainCopilot();
  const { isFeatureFlagEnabled: isNetworkFindingChainingEnabled } =
    useFeatureFlags('EXTERNAL_NETWORK_FINDING_CHAINING_ENABLED');
  const canCreateChain =
    resolveFindingsCreateChainAction({
      targetType,
      isNetworkFindingChainingEnabled,
      selectionMode: selection.mode,
      selectedCount: selection.mode === 'ids' ? selection.ids.size : 0,
    }) !== null;
  const handleCreateChain = useCallback(() => {
    if (selection.mode !== 'ids' || !canCreateChain) {
      return;
    }

    const sent = sendCreateChainToCopilot({
      selectedIds: Array.from(selection.ids),
      findings: list.findings,
    });

    if (sent) {
      selectionModel.clearSelection();
    }
  }, [
    canCreateChain,
    list.findings,
    selection,
    selectionModel,
    sendCreateChainToCopilot,
  ]);

  useEffect(() => {
    if (!list.isError) {
      return;
    }

    toasterApi.error({
      title: tTable('loadErrorTitle'),
      description: tFindingsList('loadError'),
    });
  }, [list.isError, tFindingsList, tTable]);

  const { fetchNextPage } = list;
  const { setSortState } = filters;
  const handleLoadMore = useCallback(() => {
    void fetchNextPage();
  }, [fetchNextPage]);
  const handleSortStateChange = useCallback(
    ({
      columnSort,
      groupSortOrder,
    }: {
      columnSort: FindingsColumnSort;
      groupSortOrder: FindingsGroupSortOrder;
    }) =>
      setSortState({
        sortModel: [{ colId: columnSort.colId, sort: columnSort.sort }],
        groupSortOrder,
      }),
    [setSortState],
  );
  const contextValue = useMemo<FindingsTableContextValue>(
    () => ({
      headerCheckboxState: selectionModel.headerCheckboxState,
      hasLoadedActionableFindings: selectionModel.hasLoadedActionableFindings,
      hasBulkSelection: selectionModel.hasBulkSelection,
      collapsedGroupKeys: tableModel.collapsedGroupKeys,
      rowActions: {
        onEdit: statusActions.editFinding,
        onDelete: deleteAction.requestDeleteFinding,
        onChangeStatus: statusActions.requestStatusChange,
        onTogglePublish: statusActions.togglePublish,
        onGenerateReport: reportDialog.openForFinding,
      },
      isRowSelected: selectionModel.isRowSelected,
      onSetFindingIdsSelected: selectionModel.setIdsSelected,
      onSelectAllLoaded: selectionModel.selectAllLoaded,
      onToggleGroupCollapsed: tableModel.toggleGroupCollapsed,
      onOpenAsset: navigation.openAsset,
      onOpenSignal: navigation.openSignal,
    }),
    [
      deleteAction.requestDeleteFinding,
      navigation.openAsset,
      navigation.openSignal,
      reportDialog.openForFinding,
      selectionModel.hasBulkSelection,
      selectionModel.hasLoadedActionableFindings,
      selectionModel.headerCheckboxState,
      selectionModel.isRowSelected,
      selectionModel.selectAllLoaded,
      selectionModel.setIdsSelected,
      statusActions.editFinding,
      statusActions.requestStatusChange,
      statusActions.togglePublish,
      tableModel.collapsedGroupKeys,
      tableModel.toggleGroupCollapsed,
    ],
  );
  const bulkMutationsDisabledTooltip = filters.lifecycle
    .areArchivedMutationsDisabled
    ? tLifecycle('archivedFindingActionTooltip')
    : selection.mode === 'filter' && selection.filter.wasAutoPublished
      ? tBulkConflict('autoPublishedFilterDisabledTooltip')
      : null;
  const isFiltered =
    filters.hasActiveFilters || filters.publishedFilter !== 'ALL';
  const emptyDescription = isFiltered
    ? tTable('emptyFilteredDescription')
    : endpointId
      ? tTable('emptyEndpointDescription')
      : tTable('emptyApplicationDescription');

  return (
    <FindingsTableContextProvider value={contextValue}>
      <ListPageLayout
        header={header}
        navigation={
          <FindingsContextNavigation
            targetId={targetId}
            filter={filters.filter}
            publicationChip={resolveFindingsPublicationChip(
              filters.publishedFilter,
            )}
            onPublicationChipChange={(chip) =>
              filters.setPublishedFilter(toFindingsPublishedFilter(chip))
            }
            searchTerm={filters.searchTerm}
            onSearchTermChange={filters.setSearchTerm}
            onRefresh={() => void refreshFindings()}
            isLoading={list.isInitialLoading || list.isRefetching}
            optionsMenu={
              <FindingsViewOptionsMenu
                groupByField={filters.groupByField}
                onGroupByFieldChange={filters.setGroupByField}
                areAllGroupsCollapsed={tableModel.areAllGroupsCollapsed}
                onExpandAllGroups={tableModel.expandAllGroups}
                onCollapseAllGroups={tableModel.collapseAllGroups}
              />
            }
          />
        }
        filters={
          <FindingsFilterPanel
            filters={filters}
            filteredCount={matchingCount}
            totalCount={totalCount}
            selection={
              selectionModel.selectedCount > 0 ? (
                <FindingsBulkActionsBar
                  selectedCount={selectionModel.selectedCount}
                  isSubmitting={
                    deleteAction.isSubmitting || bulkPublish.isPublishing
                  }
                  mutationsDisabledTooltip={bulkMutationsDisabledTooltip}
                  canCreateChain={canCreateChain}
                  onClearSelection={selectionModel.clearSelection}
                  onGenerateReport={reportDialog.openForSelection}
                  onCreateChain={handleCreateChain}
                  onPublish={() => void bulkPublish.publishSelection()}
                  onDelete={deleteAction.requestBulkDelete}
                />
              ) : null
            }
          />
        }
      >
        <FindingsTable
          rows={tableModel.rows}
          loadMoreRowIndex={tableModel.loadMoreRowIndex}
          findingCount={list.findings.length}
          groupByField={filters.groupByField}
          groupSortOrder={filters.groupSortOrder}
          columnSort={filters.columnSort}
          isLoading={list.isInitialLoading}
          isFetchingMore={list.isFetchingNextPage}
          hasMore={list.hasNextPage}
          emptyDescription={emptyDescription}
          onLoadMore={handleLoadMore}
          onSortStateChange={handleSortStateChange}
          onOpenFinding={navigation.openFinding}
          onOpenFindingContextMenu={navigation.openFindingContextMenu}
        />

        <FindingDeleteConfirmationDialog {...deleteAction.dialogProps} />
        <ConfirmationDialog
          isOpen={reportDialog.unpublishedConfirm.isConfirmOpen}
          onOpenChange={({ open }) => {
            if (!open) {
              reportDialog.unpublishedConfirm.cancelPending();
            }
          }}
          onConfirm={reportDialog.unpublishedConfirm.confirmPending}
          onCancel={reportDialog.unpublishedConfirm.cancelPending}
          variant="warning"
          title={tCreateReportDialog('unpublishedFindingsConfirmTitle')}
          description={tCreateReportDialog(
            'unpublishedFindingsConfirmDescription',
          )}
          confirmText={tCommon('yes')}
          cancelText={tCommon('no')}
        />
        <CreateReportDialog {...reportDialog.createDialogProps} />
        {statusActions.statusReasonDialog}
        {statusActions.signalFalsePositiveDialog}
      </ListPageLayout>
    </FindingsTableContextProvider>
  );
}
